import axios from "axios";

export type DashboardType = "admin" | "manager" | "client" | "provider";

export interface DashboardContext {
  type: DashboardType;
  userId: string;
  companyId?: string;
  clientId?: string;
  providerId?: string;
}

export interface AlertCategory {
  type: string;
  count: number;
  priority: "critical" | "warning" | "info";
  label: string;
}

export interface AlertSummary {
  totalCount: number;
  criticalCount: number;
  categories: AlertCategory[];
}

export interface KPIMetric {
  value: number;
  change: number;
  changeLabel: string;
  trend: "up" | "down" | "neutral";
  target?: number;
}

export interface KPIData {
  quotes: KPIMetric;
  revenueThisMonth: KPIMetric;
  activeProjectsValue: KPIMetric;
  marginHealth: KPIMetric & { target: number };
}

export interface RevenueTrendDataPoint {
  month: string;
  monthShort: string;
  year: number;
  revenue: number;
}

export interface RevenueTrendData {
  data: RevenueTrendDataPoint[];
  maxRevenue: number;
}

export interface PipelineFunnelStage {
  stage: string;
  label: string;
  count: number;
  value: number;
  // Optional value range (present for the RFQ stage, whose RFQs price
  // alternative options the client picks one of). `value` holds the low end.
  valueMin?: number;
  valueMax?: number;
}

export interface PipelineFunnelData {
  stages: PipelineFunnelStage[];
  maxCount: number;
}

export type PipelineTimeFilter = "all" | "month" | "quarter" | "year";

export interface RecentActivity {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string | null;
  action: string;
  actionType: string;
  entityType: string | null;
  entityId: string | null;
  entityNumber: string | null;
  /** Client / project the action was about. */
  context?: string | null;
  /** What changed, from the log metadata. */
  detail?: string | null;
  module: string | null;
  timestamp: string;
  relativeTime: string;
}

export interface RecentActivityData {
  activities: RecentActivity[];
}

export type ActionItemType = "invoice_overdue" | "invoice_due" | "quote_pending" | "project_stuck";
export type ActionItemPriority = "critical" | "warning" | "info";

export type AdminOperationCategory = "invoice_overdue" | "invoice_due_soon" | "no_project" | "no_po" | "no_invoice" | "outstanding_credit" | "provider_invoice_review";

export interface AdminOperationItem {
  id: string;
  category: AdminOperationCategory;
  priority: ActionItemPriority;
  entityType: "invoice" | "quote" | "project" | "provider_invoice";
  entityId: string;
  title: string;
  subtitle: string;
  amount?: number;
  actionLabel: string;
  createdAt: string;
}

export interface AdminOperationsData {
  totalCount: number;
  items: AdminOperationItem[];
}

export type PipelineSignalType = "deposit_paid" | "price_locked" | "quote_regenerated" | "rfq_revived" | "high_value_at_risk";

export interface PipelineSignal {
  id: string;
  signalType: PipelineSignalType;
  entityType: "quote" | "rfq";
  entityId: string;
  title: string;
  subtitle: string;
  amount?: number;
  occurredAt: string;
}

export interface PipelineSignalsData {
  totalCount: number;
  items: PipelineSignal[];
}

export interface AdminAlertSummary extends AlertSummary {
  operationsCount?: number;
  signalsCount?: number;
}

export interface ActionItem {
  id: string;
  type: ActionItemType;
  priority: ActionItemPriority;
  title: string;
  subtitle: string;
  amount: number | null;
  entityType: string;
  entityId: string;
  entityNumber: string;
  dueDate: string | null;
  daysOverdue: number | null;
}

export interface ActionItemsData {
  totalCount: number;
  items: ActionItem[];
}

export interface CalendarEvent {
  id: string;
  projectId: string;
  projectName: string;
  sequence: number;
  yardage: number | null;
  clientName: string;
  date: string;
  endDate: string | null;
  status: string;
  statusColor: string;
  address: string;
  provider: string;
  serviceType: string;
}

export interface CalendarData {
  events: CalendarEvent[];
}

export interface ClientMetricsData {
  pendingQuotes: {
    count: number;
    expiringSoon: number;
  };
  outstandingBalance: {
    amount: number;
    invoicesDue: number;
  };
  activeProjects: {
    count: number;
    changePercent: number;
  };
  ytdVolume: {
    quantity: number;
    unit: string;
    changePercent: number;
  };
}

export interface ProviderMetricsData {
  pendingPOs: {
    count: number;
    urgentCount: number;
  };
  upcomingDispatches: {
    count: number;
    timeframe: string;
  };
  mtdRevenue: {
    amount: number;
    changePercent: number;
  };
  outstandingPayments: {
    amount: number;
    overdueCount: number;
  };
}

/**
 * Fetches the dashboard context for the current user
 * Returns the dashboard type (admin, manager, client, provider) and relevant IDs
 */
export const getDashboardContext = async (): Promise<DashboardContext | null> => {
  try {
    const res = await axios.get<DashboardContext>("/dashboard/context");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching dashboard context:", e);
    return null;
  }
};

/**
 * Fetches the admin alert summary for the action required banner
 * Returns counts of items needing attention
 */
export const getAdminAlertSummary = async (): Promise<AlertSummary | null> => {
  try {
    const res = await axios.get<AlertSummary>("/dashboard/admin/alert-summary");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching admin alert summary:", e);
    return null;
  }
};

/**
 * Fetches the client alert summary for the action required banner
 * Returns counts of items needing the client's attention
 * Filtered by client's company (type 2) or individual client (type 1)
 */
export const getClientAlertSummary = async (): Promise<AlertSummary | null> => {
  try {
    const res = await axios.get<AlertSummary>("/dashboard/client/alert-summary");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching client alert summary:", e);
    return null;
  }
};

/**
 * Fetches the provider alert summary for the action required banner
 * Returns counts of items needing the provider's attention
 */
export const getProviderAlertSummary = async (): Promise<AlertSummary | null> => {
  try {
    const res = await axios.get<AlertSummary>("/dashboard/provider/alert-summary");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching provider alert summary:", e);
    return null;
  }
};

/**
 * Fetches the admin KPIs for the dashboard cards
 * Returns quotes count, revenue, active projects value, and margin health
 */
export const getAdminKPIs = async (): Promise<KPIData | null> => {
  try {
    const res = await axios.get<KPIData>("/dashboard/admin/kpis");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching admin KPIs:", e);
    return null;
  }
};

/**
 * Fetches the revenue trend data for the last 12 months
 * Returns monthly revenue data for charting
 */
export const getRevenueTrend = async (): Promise<RevenueTrendData | null> => {
  try {
    const res = await axios.get<RevenueTrendData>("/dashboard/admin/revenue-trend");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching revenue trend:", e);
    return null;
  }
};

/**
 * Fetches the pipeline funnel data
 * Returns count and value at each stage: RFQs, Quotes, Projects, Invoices
 * @param timeFilter - Filter by time period: all, month, quarter, year
 */
export const getPipelineFunnel = async (timeFilter: PipelineTimeFilter = "all"): Promise<PipelineFunnelData | null> => {
  try {
    const res = await axios.get<PipelineFunnelData>("/dashboard/admin/pipeline-funnel", {
      params: { timeFilter },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching pipeline funnel:", e);
    return null;
  }
};

/**
 * Fetches the recent activity feed for admin dashboard
 * Returns last N activities with user info and entity details
 * @param limit - Number of activities to return (default: 20)
 */
export const getRecentActivity = async (limit = 20): Promise<RecentActivityData | null> => {
  try {
    const res = await axios.get<RecentActivityData>("/dashboard/admin/recent-activity", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching recent activity:", e);
    return null;
  }
};

/**
 * Fetches action items for admin dashboard
 * Returns items requiring attention: overdue invoices, invoices due soon, stale quotes, stuck projects
 * @param limit - Number of items to return (default: 5)
 */
export const getActionItems = async (limit = 5): Promise<ActionItemsData | null> => {
  try {
    const res = await axios.get<ActionItemsData>("/dashboard/admin/action-items", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching action items:", e);
    return null;
  }
};

/**
 * Fetches admin operations items
 * Returns operational action items: overdue invoices, no-project quotes, no-PO projects, etc.
 */
export const getAdminOperations = async (maxAgeDays?: number): Promise<AdminOperationsData | null> => {
  try {
    const params = maxAgeDays ? '?maxAgeDays=' + maxAgeDays : '';
    const res = await axios.get<AdminOperationsData>("/dashboard/admin/operations" + params);
    return res.data;
  } catch (e: any) {
    console.error("Error fetching admin operations:", e);
    return null;
  }
};

/**
 * Fetches admin pipeline signals
 * Returns recent client activity: deposits, locks, regenerations, revivals, at-risk quotes
 */
export const getAdminPipelineSignals = async (): Promise<PipelineSignalsData | null> => {
  try {
    const res = await axios.get<PipelineSignalsData>("/dashboard/admin/pipeline-signals");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching pipeline signals:", e);
    return null;
  }
};

/**
 * Fetches action items for client dashboard
 * Returns items requiring the client's attention: overdue invoices, invoices due soon, quotes awaiting response
 * Filtered by client's company (type 2) or individual client (type 1)
 * @param limit - Number of items to return (default: 5)
 */
export const getClientActionItems = async (limit = 5): Promise<ActionItemsData | null> => {
  try {
    const res = await axios.get<ActionItemsData>("/dashboard/client/action-items", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching client action items:", e);
    return null;
  }
};

/**
 * Fetches calendar events for admin/manager dashboard
 * Returns scheduled projects for a given month
 * @param month - Month in YYYY-MM format (default: current month)
 * @param role - Dashboard role: 'admin' or 'manager' (default: 'admin')
 */
export const getCalendarEvents = async (month?: string, role: 'admin' | 'manager' = 'admin'): Promise<CalendarData | null> => {
  try {
    const endpoint = role === 'manager' ? '/dashboard/manager/calendar' : '/dashboard/admin/calendar';
    const res = await axios.get<CalendarData>(endpoint, {
      params: { month },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching calendar events:", e);
    return null;
  }
};

/**
 * Fetches client metrics for KPI cards
 * Returns pending quotes, outstanding balance, active projects, and YTD volume
 */
export const getClientMetrics = async (): Promise<ClientMetricsData | null> => {
  try {
    const res = await axios.get<ClientMetricsData>("/dashboard/client/metrics");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching client metrics:", e);
    return null;
  }
};

export interface ClientProject {
  id: string;
  projectNumber: string;
  projectName: string;
  status: string;
  statusColor: string;
  serviceDate: string | null;
  locationAddress: string;
}

export interface ClientProjectsData {
  totalCount: number;
  projects: ClientProject[];
}

/**
 * Fetches client projects for My Projects section
 * Returns active projects with status badges
 * @param limit - Number of projects to return (default: 6)
 */
export const getClientProjects = async (limit = 6): Promise<ClientProjectsData | null> => {
  try {
    const res = await axios.get<ClientProjectsData>("/dashboard/client/projects", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching client projects:", e);
    return null;
  }
};

export type QuoteUrgency = "critical" | "warning" | "normal";

export interface PendingQuote {
  id: string;
  quoteNumber: string;
  projectName: string;
  amount: number;
  sentDate: string;
  daysPending: number;
  urgency: QuoteUrgency;
  status?: number;
  priceLockDepositStatus?: string | null;
  priceLockDepositAmount?: number | null;
  priceLockExpiresAt?: string | null;
  expiresAt?: string | null;
}

export interface PendingQuotesData {
  totalCount: number;
  quotes: PendingQuote[];
}

export interface PendingPO {
  id: string;
  poNumber: string;
  projectName: string;
  amount: number;
  quantity: number;
  unit: string;
  serviceDate: string;
  sentDate: string;
  daysPending: number;
  urgency: QuoteUrgency;
}

export interface PendingPOsData {
  totalCount: number;
  purchaseOrders: PendingPO[];
}

/**
 * Fetches client pending quotes for Quotes Awaiting Response section
 * Returns quotes pending client approval with urgency indicators
 * @param limit - Number of quotes to return (default: 5)
 */
export const getClientPendingQuotes = async (limit = 5): Promise<PendingQuotesData | null> => {
  try {
    const res = await axios.get<PendingQuotesData>("/dashboard/client/pending-quotes", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching client pending quotes:", e);
    return null;
  }
};

/**
 * Fetches provider metrics for KPI cards
 * Returns pending POs, upcoming dispatches, MTD revenue, and outstanding payments
 */
export const getProviderMetrics = async (): Promise<ProviderMetricsData | null> => {
  try {
    const res = await axios.get<ProviderMetricsData>("/dashboard/provider/metrics");
    return res.data;
  } catch (e: any) {
    console.error("Error fetching provider metrics:", e);
    return null;
  }
};

/**
 * Fetches provider pending POs for Purchase Orders Awaiting Response section
 * Returns POs pending provider approval with urgency indicators
 * @param limit - Number of POs to return (default: 5)
 */
export const getProviderPendingPOs = async (limit = 5): Promise<PendingPOsData | null> => {
  try {
    const res = await axios.get<PendingPOsData>("/dashboard/provider/pending-pos", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching provider pending POs:", e);
    return null;
  }
};

export interface UpcomingDelivery {
  id: string;
  poId: string;
  poNumber: string;
  projectName: string;
  scheduleDate: string;
  scheduleTime: string | null;
  quantity: number;
  unit: string;
  status: "accepted" | "sent";
  address: string;
}

export interface UpcomingDeliveriesData {
  totalCount: number;
  deliveries: UpcomingDelivery[];
}

/**
 * Fetches provider upcoming deliveries for Upcoming Deliveries section
 * Returns accepted POs with scheduled delivery dates in the next 14 days
 * @param limit - Number of deliveries to return (default: 5)
 */
export const getProviderUpcomingDeliveries = async (limit = 5): Promise<UpcomingDeliveriesData | null> => {
  try {
    const res = await axios.get<UpcomingDeliveriesData>("/dashboard/provider/upcoming-deliveries", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching provider upcoming deliveries:", e);
    return null;
  }
};

/**
 * Fetches provider action items
 * Returns pending POs and overdue payments requiring attention
 * @param limit - Number of items to return (default: 5)
 */
export const getProviderActionItems = async (limit = 5): Promise<ActionItemsData | null> => {
  try {
    const res = await axios.get<ActionItemsData>("/dashboard/provider/action-items", {
      params: { limit },
    });
    return res.data;
  } catch (e: any) {
    console.error("Error fetching provider action items:", e);
    return null;
  }
};

// ---------------------------------------------------------------------
// Dashboard redesign - the aggregated overview endpoint
// ---------------------------------------------------------------------
// One request for the whole performance block, per DASHBOARD_REDESIGN_SPEC
// section 7.15. Definitions for every figure below live in
// js_crm_backend/docs/dashboard/METRIC_DEFINITIONS.md - do not re-derive
// any of them in the UI.

export type MetricPeriodType = "month" | "quarter" | "year" | "custom";

export interface ResolvedPeriod {
  type: MetricPeriodType;
  label: string;
  timezone: string;
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  /** True when the period has not finished yet. */
  partial: boolean;
}

export interface MetricCard {
  value: number;
  count: number;
  previousValue: number;
  changePercent: number | null;
}

export interface MarginCard {
  value: number | null;
  costableRevenue: number;
  cost: number;
  previousValue: number | null;
  changePoints: number | null;
  uncostableCount: number;
  uncostableRevenue: number;
}

export interface TrendPoint {
  month: string;
  label: string;
  invoiced: number;
  collected: number;
}

export interface ArBucket {
  key: "current" | "d1_30" | "d31_60" | "d61_90" | "d90_plus";
  label: string;
  amount: number;
  count: number;
}

export interface ArAging {
  total: number;
  count: number;
  buckets: ArBucket[];
  oldest: Array<{
    invoiceId: string;
    sequence: number;
    clientName: string | null;
    balance: number;
    dueDate: string | null;
    daysPastDue: number;
  }>;
}

export interface UnsentDrafts {
  count: number;
  value: number;
  oldestCreatedAt: string | null;
}

export interface WorkQueueItem {
  id: string;
  kind: "money" | "billing" | "client" | "system";
  severity: "critical" | "warning" | "info";
  title: string;
  reason: string;
  amount: number | null;
  actionLabel: string;
  /** Where the action goes. Every row carries one. */
  actionPath: string;
  entityType: string | null;
  entityId: string | null;
}

export interface PlanProfitability {
  period: ResolvedPeriod;
  plans: PlanRow[];
  totals: {
    billed: number;
    collected: number;
    cost: number;
    margin: number | null;
  };
}

export interface ProviderPerformanceRow {
  providerId: string;
  name: string;
  orders: number;
  spend: number;
  avgDaysToAccept: number | null;
  unaccepted: number;
  matchRate: number | null;
}

export interface ProviderPerformance {
  period: ResolvedPeriod;
  providers: ProviderPerformanceRow[];
}

export const getPlanProfitability = async (
  period: MetricPeriodType = "year",
): Promise<PlanProfitability> => {
  const res = await axios.get<PlanProfitability>(
    "/dashboard/admin/reports/plan-profitability",
    { excludeInterceptor: true, params: { period } } as any,
  );
  return res.data;
};

/** Operator-only. Never build a provider-facing view on this (spec 7.10). */
export const getProviderPerformance = async (
  period: MetricPeriodType = "year",
): Promise<ProviderPerformance> => {
  const res = await axios.get<ProviderPerformance>(
    "/dashboard/admin/reports/provider-performance",
    { excludeInterceptor: true, params: { period } } as any,
  );
  return res.data;
};

export interface ReminderResult {
  sent: boolean;
  recipient: string | null;
  throttledUntil: string | null;
}

/**
 * Send a reminder about one record. Sends a real email, so callers
 * confirm first.
 */
export const sendReminder = async (
  entityType: "invoice" | "quote" | "purchase_order",
  entityId: string,
): Promise<ReminderResult> => {
  const res = await axios.post<ReminderResult>(
    "/follow-ups/" + entityType + "/" + entityId + "/remind",
    {},
    { excludeInterceptor: true } as any,
  );
  return res.data;
};

export interface PlanInterestResult {
  recorded: boolean;
  alreadyRequested: boolean;
}

/**
 * A client asking about a maintenance plan. Records the interest and
 * notifies the team; creates no plan and no commitment.
 */
export const recordPlanInterest = async (
  note?: string,
): Promise<PlanInterestResult> => {
  const res = await axios.post<PlanInterestResult>(
    "/dashboard/client/plan-interest",
    { note: note || null },
    { excludeInterceptor: true } as any,
  );
  return res.data;
};

export interface QueueRecords {
  rowId: string;
  title: string;
  explanation: string;
  /**
   * Set only for the rows whose condition is a past event — plan
   * interest and proposal follow-up. Everything else is derived from a
   * live condition and must be FIXED rather than dismissed, so the
   * control is absent rather than disabled.
   */
  dismissable?: boolean;
  /** Dismiss button text when "Mark handled" is the wrong word. */
  dismissLabel?: string;
  /** possible-redo only: records can be retired from the list. */
  retirable?: boolean;
  records: Array<{
    id: string;
    label: string;
    detail: string;
    /** The date in the detail line needs action (passed or missing). */
    urgent?: boolean;
    path: string;
    /**
     * Money records only. When present the drill-down gives the figure
     * its own right-aligned column and subtotals each band, instead of
     * burying the number in `detail`.
     */
    amount?: number;
    /**
     * Bands the list by next action. `groupOrder` sorts the bands — most
     * actionable first, not pipeline order. Absent on every existing
     * drill-down, which keeps rendering as one flat list.
     */
    group?: string;
    groupOrder?: number;
  }>;
}

/**
 * The records behind a work-queue row. Only the integrity rows use
 * this - everything else links to a list that already exists.
 */
export const getQueueRecords = async (
  rowId: string,
): Promise<QueueRecords> => {
  const res = await axios.get<QueueRecords>(
    "/dashboard/admin/queue/" + rowId + "/records",
    { excludeInterceptor: true } as any,
  );
  return res.data;
};

/**
 * Mark one record behind a queue row as handled.
 *
 * The server refuses any row that clears itself — dismissing an overdue
 * invoice would hide the work instead of finishing it — so a 400 here
 * means the row was never meant to be dismissable, not that the call
 * failed.
 */
export const dismissQueueRecord = async (
  rowId: string,
  recordId: string,
): Promise<{ dismissed: true } | { message?: string }> => {
  try {
    const res = await axios.post(
      "/dashboard/admin/queue/" + rowId + "/records/" + recordId + "/dismiss",
      {},
    );
    return res?.data ?? {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : {};
  }
};

/**
 * Retire a replaced quote chain (possible-redo row): quote superseded,
 * open POs cancelled, draft invoices voided. The server re-checks that it
 * is still a candidate.
 */
export const retireRedoQuote = async (
  recordId: string,
): Promise<
  | { retired: true; voidedInvoices: string[]; cancelledPOs: string[] }
  | { message?: string }
> => {
  try {
    const res = await axios.post(
      "/dashboard/admin/queue/possible-redo/records/" + recordId + "/retire",
      {},
    );
    return res?.data ?? {};
  } catch (e: any) {
    return e?.response?.data ? e.response.data : {};
  }
};

export interface WorkQueueGroup {
  kind: "money" | "billing" | "client" | "system";
  label: string;
  count: number;
  amount: number | null;
  items: WorkQueueItem[];
}

export interface WorkQueue {
  totalCount: number;
  groups: WorkQueueGroup[];
}

export interface SupplySide {
  committed: { value: number; count: number; windowDays: number };
  neverInvoiced: { count: number; value: number; oldestCreatedAt: string | null };
  awaitingApproval: { count: number; value: number };
  variance: { count: number; value: number };
  concentration: {
    windowDays: number;
    totalSpend: number;
    topProviderName: string | null;
    topProviderSpend: number;
    sharePercent: number | null;
    providers: Array<{ name: string; spend: number; purchaseOrders: number }>;
  };
}

export interface PlanRow {
  planId: string;
  name: string;
  status: string;
  endDate: string | null;
  committedSiteCount: number | null;
  sites: number;
  workDone: number;
  sitesBilled: number;
  billed: number;
  collected: number;
  cost: number;
  margin: number | null;
}

export interface ScheduleItem {
  type: "work" | "invoice_due";
  title: string;
  subtitle: string;
  amount: number | null;
  entityType: string;
  entityId: string;
}

export interface ScheduleDay {
  date: string;
  weekday: string;
  dayOfMonth: number;
  isToday: boolean;
  items: ScheduleItem[];
}

export interface DashboardOverviewData {
  period: ResolvedPeriod;
  approvedActive: MetricCard;
  invoiced: MetricCard;
  collected: MetricCard;
  margin: MarginCard;
  trend: TrendPoint[];
  ar: ArAging;
  unsentDrafts: UnsentDrafts;
  /**
   * Value of the ACTIVE PROJECTS — sold and not yet invoiced. Live, and
   * not affected by the period filter. Excludes Invoice Sent, whose
   * money is already in `ar`.
   */
  activeProjects?: {
    value: number;
    projects: number;
    awaitingSchedule: number;
  } | null;
  workQueue: WorkQueue;
  supply: SupplySide;
  plans: PlanRow[];
  schedule: ScheduleDay[];
}

export interface PlanFinancials {
  planId: string;
  approved: { value: number; count: number };
  invoiced: { value: number; count: number };
  /** Provider cost on BILLED work only — the denominator of `margin`. */
  cost: number;
  /**
   * Provider cost on plan work the client has NOT been invoiced for.
   * Outside `cost` and outside `margin` on purpose (Session 81): it used
   * to be inside both, which made the margin of any live plan
   * meaningless. A real exposure, not a loss.
   */
  committedUnbilled: number;
  collected: number;
  margin: number | null;
  unquotedWorkOrders: number;
}

/**
 * Plan-level money for the Plan Detail header. OPERATOR ONLY - the
 * endpoint is isCompany-gated because it returns cost and margin, and
 * there is deliberately no client-facing equivalent.
 */
export const getPlanFinancials = async (
  planId: string,
): Promise<PlanFinancials> => {
  const res = await axios.get<PlanFinancials>(
    "/dashboard/admin/plans/" + planId + "/financials",
    { excludeInterceptor: true } as any,
  );
  return res.data;
};

export interface ClientOverviewData {
  period: ResolvedPeriod;
  approved: MetricCard;
  invoiced: MetricCard;
  plans: Array<{
    planId: string;
    name: string;
    sites: number;
    worked: number;
  }>;
  decisions: WorkQueueItem[];
}

export interface ProviderOverviewData {
  period: ResolvedPeriod;
  awarded: MetricCard;
  awaitingPayment: { value: number; count: number };
  queue: WorkQueueItem[];
}

/**
 * Client-facing overview. The server decides which client this is from
 * the session - there is no id to pass, deliberately.
 *
 * NO MARGIN, NO COST, NO PROVIDER IDENTITY comes back from this
 * endpoint. That is enforced server-side; the UI does not hide it.
 */
export const getClientOverview = async (
  period: MetricPeriodType = "year",
): Promise<ClientOverviewData> => {
  const res = await axios.get<ClientOverviewData>("/dashboard/client/overview", {
    excludeInterceptor: true,
    params: { period },
  } as any);
  return res.data;
};

/** Provider-facing overview. Their own cost only; never our margin. */
export const getProviderOverview = async (
  period: MetricPeriodType = "year",
): Promise<ProviderOverviewData> => {
  const res = await axios.get<ProviderOverviewData>(
    "/dashboard/provider/overview",
    {
      excludeInterceptor: true,
      params: { period },
    } as any,
  );
  return res.data;
};

export interface OverviewParams {
  period?: MetricPeriodType;
  /** YYYY-MM-DD, required when period is "custom". */
  from?: string;
  /** YYYY-MM-DD, inclusive. */
  to?: string;
}

/**
 * Fetches the whole performance block in one request.
 *
 * Rethrows rather than swallowing to null, unlike the older calls in this
 * file. The overview is the page, not a widget on it: a silent null would
 * render an empty dashboard that looks like a business with no work in it,
 * which is worse than an error state.
 */
export const getDashboardOverview = async (
  params: OverviewParams = {},
): Promise<DashboardOverviewData> => {
  const res = await axios.get<DashboardOverviewData>("/dashboard/admin/overview", {
    // EXCLUDED FROM THE GLOBAL INTERCEPTOR ON PURPOSE.
    //
    // The response interceptor in App.tsx turns ANY 404 on a GET into a
    // full-page NotFound route - it replaces the whole app, not the
    // panel that failed. A dashboard panel whose endpoint is missing
    // (not yet deployed to this environment, say) would therefore take
    // the entire dashboard down with it, which is what happened the
    // first time this shipped ahead of the backend.
    //
    // This call handles its own failure: DashboardOverview renders an
    // error banner and the rest of the page keeps working.
    excludeInterceptor: true,
    params: {
      period: params.period || "month",
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
    },
  } as any);
  return res.data;
};
